<template>
  <NewStudioModalsModalShell title="Your Designs" class="your-designs-modal" data-figma-node="2913:177374" @close="$emit('close')">
    <p class="designs-intro">Pick up where you left off, or share your work.</p>
    <p v-if="shareMessage" class="share-feedback" :class="`share-feedback--${shareState}`" :role="shareState === 'error' ? 'alert' : 'status'">{{ shareMessage }}</p>
    <p v-if="error" role="alert">{{ error }}</p>
    <div class="design-list">
      <article v-for="design in designs" :key="design.id" data-figma-card-node="2913:177200">
        <img v-if="design.imageUrl || design.base64Image || design.imagePreview" :src="design.imageUrl || design.base64Image || design.imagePreview" alt="">
        <div v-else class="placeholder"><img src="/new-studio/icons/image-placeholder.svg" alt=""><span>Loading preview…</span></div>
        <button class="delete" type="button" aria-label="Delete design" @click="remove(design.id)"><img src="/new-studio/icons/delete.svg" alt=""></button>
        <div class="design-actions">
        <button type="button" @click="open(design.id)">Open in Design Studio</button>
        <button type="button" @click="share(design.id)">Share</button>
        </div>
      </article>
    </div>
    <!-- Off-screen stages for generating previews -->
    <div v-if="designsNeedingPreview.length > 0" class="preview-offscreen" aria-hidden="true">
      <v-stage
        v-for="design in previewBatch"
        :key="design.id"
        ref="stageRefs"
        :config="{ width: 728, height: 323, pixelRatio: 1, scaleX: 0.5, scaleY: 0.5, id: design.id }"
      >
        <v-layer :config="{ id: 'shapes-layer', name: 'shapes-layer' }">
          <component
            :is="getShapeComponent(shape.type)"
            v-for="shape in design.shapeList"
            :key="shape.id"
            :config="shape"
            :draggable="false"
          />
        </v-layer>
      </v-stage>
    </div>
    <button v-if="nextPage > 1" class="more" type="button" :disabled="loading" @click="load">Load More</button>
    <p v-if="!loading && !designs.length">No saved designs yet.</p>
  </NewStudioModalsModalShell>
</template>

<script setup lang="ts">
import { copyTextWithFallback } from '../logic'
import { getShapeComponent } from '~/components/CanvasGrid/helpers'
import { transformShapeForAdd } from '~/composables/shapes/transform'

defineEmits<{ close: [] }>()
const userStore = useUserStore()
const projectStore = useProjectStore()
const gridStore = useGridStore()
const modalStore = useModalStore()
interface SavedDesign { id: string; design_name?: string; imageUrl?: string; base64Image?: string; imagePreview?: string; shapeList?: any[]; shapes?: any[] }
const designs = ref<SavedDesign[]>([])
const nextPage = ref(1)
const loading = ref(false)
const error = ref('')
const shareState = ref<'success' | 'error'>('success')
const shareMessage = ref('')
const stageRefs = ref<any[]>([])

const designsNeedingPreview = computed(() =>
  designs.value.filter(d => !d.imageUrl && !d.base64Image && !d.imagePreview && d.shapeList?.length)
)
const previewBatch = computed(() => designsNeedingPreview.value.slice(0, 5))

const capturePreviewImages = () => {
  if (!stageRefs.value?.length) return
  const refs = Array.isArray(stageRefs.value) ? stageRefs.value : [stageRefs.value]
  for (const ref of refs) {
    const stage = ref?.getStage?.() || ref?.getNode?.()?.getStage?.()
    if (!stage) continue
    const id = stage.id()
    const design = designs.value.find(d => d.id === id)
    if (!design || design.imagePreview) continue
    const dataURL = stage.toDataURL({ pixelRatio: 1, width: 728, height: 323 })
    if (dataURL && dataURL.length > 100) design.imagePreview = dataURL
  }
  // Process next batch if there are more
  if (designsNeedingPreview.value.length > 0) {
    nextTick(() => nextTick(capturePreviewImages))
  }
}

const load = async () => {
  loading.value = true; error.value = ''
  try {
    const response = await userStore.fetchDesigns(nextPage.value)
    // Transform shapes for designs without images
    const processed = await Promise.all(response.designs.map(async (design: any) => {
      if (design.imageUrl || design.base64Image) return design
      if (!design.shapes?.length) return design
      try {
        const shapeList = (await Promise.all(
          design.shapes.map((shape: any) => transformShapeForAdd(shape).catch(() => null))
        )).filter(Boolean)
        return { ...design, shapeList }
      } catch { return design }
    }))
    designs.value.push(...processed)
    nextPage.value = response.pagination.totalPages > response.pagination.page ? response.pagination.page + 1 : 1
    // Capture previews after stages render
    nextTick(() => nextTick(capturePreviewImages))
  } catch { error.value = 'Unable to load your designs.' }
  finally { loading.value = false }
}
const open = async (id: string) => {
  try {
    const design = await projectStore.load(id)
    if (!design) throw new Error()
    gridStore.resetGrid(); await gridStore.loadDesign(design); modalStore.handleSuccess(ModalType.MyDesigns)
  } catch { error.value = 'Unable to open this design.' }
}
const share = async (id: string) => {
  const copied = await copyTextWithFallback(`${window.location.origin}/?import=${id}`)
  shareState.value = copied ? 'success' : 'error'
  shareMessage.value = copied ? 'Share link copied to clipboard.' : 'Unable to copy share link. Please try again.'
}
const remove = async (id: string) => {
  if (!window.confirm('Delete this design? This cannot be undone.')) return
  try { await $fetch(`/api/designs/${id}`, { method: 'DELETE' }); designs.value = designs.value.filter((design) => design.id !== id) }
  catch { error.value = 'Unable to delete this design.' }
}
onMounted(load)
</script>

<style scoped>
.designs-intro { margin: 0 0 34.203125px; text-align: center; color: #55554f; font-size: 16px; font-weight: 600; }
.design-list { display: grid; gap: 28px; }
article { position: relative; display: flex; width: 728px; flex-direction: column; }
article img, .placeholder { width: 728px; height: 323px; object-fit: cover; background: #efeee9; }
.placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; color: rgb(0 0 0 / 55%); font-family: Inter, sans-serif; }
.placeholder > img { width: 28px; height: 28px; margin-bottom: 5px; }
.placeholder span { font-size: 12.8px; line-height: 16.9px; letter-spacing: .13px; }
.placeholder small { font-size: 10.3px; line-height: 14.3px; }
.design-actions { display: grid; width: 100%; height: 48px; overflow: hidden; grid-template-columns: 1fr 1fr; }
button { height: 48px; min-height: 48px; padding: 0 13px; border: 0; background: #2e3a46; color: #fff; font: inherit; font-size: 18px; font-weight: 700; cursor: pointer; }
.design-actions button:last-child { background: #313558; }
button.delete { position: absolute; z-index: 1; top: 10px; right: 10px; display: block; width: 27px; height: 27px; min-height: 27px; padding: 0; border: 0; background: transparent; box-shadow: none; }
button.delete img { display: block; width: 27px; height: 27px; }
.more { width: 100%; margin-top: 18px; }
p[role='alert'] { color: #a32121; }
.share-feedback { position: absolute; z-index: 3; top: 82px; left: 30px; width: 728px; margin: 0; padding: 7px 12px; background: #edf7ed; color: #246b2d; text-align: center; font-size: 12px; font-weight: 600; }
.share-feedback--error { background: #fff0f0; color: #a32121; }
.preview-offscreen { position: absolute; left: -9999px; top: -9999px; pointer-events: none; opacity: 0; }
:global(.new-modal__dialog.your-designs-modal) { height: 749px; min-height: 749px; max-height: 749px; padding: 30px; overflow-y: auto; }
:global(.your-designs-modal > .new-modal__close) { top: 22px; right: 22px; }
</style>
